import { Info, Warning } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAllEntities, useRootEntities } from 'api/entity';
import { useFieldTypes } from 'api/fieldEditor';
import { ProjectsList } from 'components/ProjectsList';
import { plural } from 'pluralize';
import React, { useMemo, useState } from 'react';
import { formatName } from 'utils/fields';

export default function RootEntitiesPage() {
  const { data: entityTypes, isLoading, error } = useFieldTypes();
  const [selectedEntityType, setSelectedEntityType] = useState<string | null>(
    null,
  );

  const [filter, setFilter] = useState<'root' | 'all'>('root');
  const [textFilter, setTextFilter] = useState<string>('');

  if (isLoading) {
    return <Typography>Loading entity types...</Typography>;
  }

  if (error) {
    return (
      <Typography color="error">
        Error loading entity types: {error.message}
      </Typography>
    );
  }

  return (
    <Stack gap={2}>
      <Typography variant="h4" gutterBottom>
        Entity Types
      </Typography>
      <Autocomplete
        options={entityTypes?.map((x) => x.type) || []}
        getOptionLabel={(option) => formatName(option)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Entity Type"
            variant="outlined"
          />
        )}
        onChange={(event, value) => {
          setSelectedEntityType(value ? value : null);
        }}
        value={selectedEntityType}
      />

      {selectedEntityType ? (
        <>
          <Typography variant="h5" gutterBottom>
            {formatName(selectedEntityType)} Entities
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6">
              {plural(formatName(selectedEntityType))}
            </Typography>
            <TextField
              select
              variant="standard"
              InputProps={{
                startAdornment: (
                  <Typography variant="body2" pr={0.5}>
                    Filter:
                  </Typography>
                ),
              }}
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
            >
              <MenuItem value="root">Root</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </TextField>
          </Stack>

          <TextField
            label="Filter entities"
            variant="outlined"
            fullWidth
            placeholder="Type to filter entities by name"
            value={textFilter}
            onChange={(e) => setTextFilter(e.target.value)}
            size="small"
          />

          {filter === 'root' && (
            <RootEntitiesAlert
              type={selectedEntityType}
              textFilter={textFilter}
              onViewAll={() => setFilter('all')}
            />
          )}

          {filter === 'root' ? (
            <RootEntities type={selectedEntityType} textFilter={textFilter} />
          ) : (
            <AllEntities type={selectedEntityType} textFilter={textFilter} />
          )}
        </>
      ) : (
        <Typography color="textSecondary" align="center" sx={{ py: 5 }}>
          Select an entity type from the left to view its entities.
        </Typography>
      )}
    </Stack>
  );
}

function RootEntities(props: { type: string; textFilter: string }) {
  const entities = useRootEntities(props.type);

  const filteredEntities = useMemo(() => {
    if (!entities.data?.length) {
      return [];
    }

    if (!props.textFilter) {
      return entities.data;
    }

    const filterLower = props.textFilter.toLowerCase();
    return entities.data.filter(
      (entity) =>
        entity.name.toLowerCase().includes(filterLower) ||
        entity.description?.toLowerCase().includes(filterLower),
    );
  }, [entities.data, props.textFilter]);

  if (!filteredEntities.length) {
    return (
      <Typography color="textSecondary" align="center" sx={{ py: 2 }}>
        No matching entities found
      </Typography>
    );
  }

  return (
    <ProjectsList
      entityViewProps={{ hideParent: true }}
      entities={filteredEntities}
    />
  );
}

function AllEntities(props: { type: string; textFilter: string }) {
  const entities = useAllEntities(props.type);

  const filteredEntities = useMemo(() => {
    if (!entities.data?.length) {
      return [];
    }

    if (!props.textFilter) {
      return entities.data;
    }

    const filterLower = props.textFilter.toLowerCase();
    return entities.data.filter(
      (entity) =>
        entity.name.toLowerCase().includes(filterLower) ||
        entity.description?.toLowerCase().includes(filterLower),
    );
  }, [entities.data, props.textFilter]);

  if (!filteredEntities.length) {
    return (
      <Typography color="textSecondary" align="center" sx={{ py: 2 }}>
        No matching entities found
      </Typography>
    );
  }

  return (
    <ProjectsList
      entityViewProps={{ hideParent: true }}
      entities={filteredEntities}
    />
  );
}

function RootEntitiesAlert(props: {
  type: string;
  textFilter: string;
  onViewAll: () => void;
}) {
  const entities = useRootEntities(props.type);

  // Check if there are no entities or if filtering resulted in no matches
  const hasNoEntities = !entities.data?.length;

  // If we're still loading, don't show any alert
  if (entities.isLoading) {
    return null;
  }

  // If there are no root entities, show warning alert
  if (hasNoEntities) {
    return (
      <Alert
        severity="warning"
        icon={<Warning />}
        action={
          <Button color="inherit" size="small" onClick={props.onViewAll}>
            View All
          </Button>
        }
      >
        {`No ${plural(formatName(props.type)).toLowerCase()} exist at the root level. Click "View All" to see all ${plural(
          formatName(props.type),
        ).toLowerCase()}.`}
      </Alert>
    );
  }

  // Otherwise show the normal info alert
  return (
    <Alert
      severity="info"
      icon={<Info />}
      action={
        <Button color="inherit" size="small" onClick={props.onViewAll}>
          Show All
        </Button>
      }
    >
      {`Showing only the top-level ${plural(formatName(props.type)).toLowerCase()}.`}
    </Alert>
  );
}
